import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { HERO_ART } from '../config/heroArt'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'
import { joinWaitlist } from '../lib/content'
import { trackNewsletterSignup } from '../utils/track'

// The one URL to hand out for the owned list: LL slides, talk closers, the labs
// README, anywhere off-site. Signups land in the same WaitlistEntry table as the
// /about section and the in-article forms, so the admin shows one pool.
//
// `?src=` tags where the signup came from and shows up as the source chip in the
// admin Waitlist page (e.g. /newsletter?src=ll-agents-jul). Unknown values are
// slugified rather than rejected, but keep the list in SOURCE_LABELS short so the
// admin filter stays readable. No src means someone found the page on their own.
const DEFAULT_SOURCE = 'newsletter-page'

function normalizeSource(raw: string | null): string {
  if (!raw) return DEFAULT_SOURCE
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  return slug ? `newsletter-page:${slug}` : DEFAULT_SOURCE
}

export default function NewsletterPage() {
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setStatus('loading')
    setError('')
    try {
      const source = normalizeSource(params.get('src'))
      await joinWaitlist(email.trim(), source)
      // After the row exists, so a rejected address is never counted as a signup.
      trackNewsletterSignup(source)
      setStatus('done')
      setEmail('')
    } catch (err) {
      setError((err as Error).message)
      setStatus('error')
    }
  }

  return (
    <div className="bg-gray-50 min-h-screen text-[#232323]">
      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="newsletter-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="max-w-3xl">
              <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
                Notes from Gazar
              </h1>
              <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body">
                Occasional email on system design and shipping AI, written for engineers moving from
                senior to staff. No fixed schedule, no fluff, just the ideas worth keeping.
              </p>
            </div>
            <HeroArtGrid images={HERO_ART.contact} />
          </div>
        </ContentColumn>
      </section>

      <section className="border-b border-border-subtle bg-white">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-label font-body text-gray-900/60">
              <li>
                <Link
                  to="/"
                  className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                >
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Newsletter
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <ContentColumn
          anchor="newsletter-signup"
          className="max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8"
        >
          <div className="rounded-smooth-lg border border-border-strong bg-white p-6 sm:p-10">
            {status === 'done' ? (
              <div className="space-y-3">
                <h2 className="font-heading text-2xl font-bold text-brand-navy">
                  You&rsquo;re on the list.
                </h2>
                <p className="text-lg leading-[1.58] text-ink-soft font-body">
                  Thanks for subscribing. Nothing else to do, the next one will land in your inbox.
                </p>
                <p className="font-body text-ink-soft">
                  In the meantime,{' '}
                  <Link to="/newsletter/archive" className="text-brand-red underline hover:no-underline">
                    read the past issues
                  </Link>{' '}
                  or{' '}
                  <Link to="/courses" className="text-brand-red underline hover:no-underline">
                    see what&rsquo;s running
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-navy leading-[1.2] tracking-[-0.02em]">
                  Get the email
                </h2>
                <div className="h-1 w-20 bg-brand-red mt-4"></div>

                <ul className="mt-8 space-y-3 list-disc pl-5 marker:text-brand-red font-body text-lg leading-[1.58] text-ink-soft">
                  <li>Architecture decisions written up the way I actually made them, trade-offs included.</li>
                  <li>What the senior to staff jump really asks for, from someone still doing it.</li>
                  <li>Early notice when a cohort, course, or free live session opens.</li>
                </ul>

                {/* Three bullets are a claim. A back issue is the thing itself,
                    and it costs nothing to let somebody check before they hand
                    over an address. */}
                <p className="mt-6 font-body text-lg leading-[1.58] text-ink-soft">
                  Rather see one first?{' '}
                  <Link to="/newsletter/archive" className="text-brand-red underline hover:no-underline">
                    Read the back issues
                  </Link>
                  .
                </p>

                <form onSubmit={submit} className="mt-8 space-y-4">
                  <label
                    htmlFor="newsletter-page-email"
                    className="block font-heading text-sm font-bold uppercase tracking-[0.14em] text-brand-navy"
                  >
                    Email address
                  </label>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      id="newsletter-page-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="flex-1 rounded-smooth border border-border-strong px-4 py-3 font-body text-brand-navy focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20"
                    />
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="rounded-smooth bg-brand-navy px-8 py-3 font-heading font-bold text-white transition-colors hover:bg-brand-navy/90 disabled:opacity-60"
                    >
                      {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
                    </button>
                  </div>
                  {status === 'error' ? (
                    <p className="text-sm text-brand-red font-body" role="alert">
                      {error}
                    </p>
                  ) : null}
                  <p className="text-sm text-ink-soft/70 font-body">
                    By subscribing you agree to receive occasional emails from me. No spam, no
                    sharing your address with anyone, unsubscribe from any email. See the{' '}
                    <Link to="/privacy" className="underline hover:no-underline">
                      privacy policy
                    </Link>
                    .
                  </p>
                </form>
              </>
            )}
          </div>
        </ContentColumn>
      </section>
    </div>
  )
}
